import { useEffect, useState } from "react";
import { X, Mail, CheckCircle2, AlertTriangle, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const CATEGORY_LABEL = {
  milestone: "Milestone",
  monthly_report: "Monthly report",
  alert: "Alert",
  admin_alert: "Admin alert",
  trial_reminder: "Trial reminder",
  trial_day3: "Trial · Day 3 check-in",
  trial_day6: "Trial · Day 6 summary",
  password_reset: "Password reset",
  upgrade_confirmation: "Upgrade",
  cancellation_confirmation: "Cancellation",
  team_invite: "Team invite",
  owner_digest: "Owner digest",
  contact_form: "Contact form",
  lead_notification: "New lead",
  free_scan_report: "Free-scan report",
  free_scan_followup: "Free-scan follow-up",
  first_hour_checkin: "First-hour check-in",
  resend: "Resend",
  other: "Other",
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
};

export function EmailPreviewModal({ emailId, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendTo, setResendTo] = useState("");
  const [resending, setResending] = useState(false);
  const [showResendForm, setShowResendForm] = useState(false);

  useEffect(() => {
    if (!open || !emailId) return;
    setLoading(true);
    setError(null);
    setDetail(null);
    setShowResendForm(false);
    setResendTo("");
    api.get(`/admin/email-log/${emailId}`)
      .then((res) => setDetail(res.data))
      .catch((err) => setError(err.response?.data?.detail || "Could not load email"))
      .finally(() => setLoading(false));
  }, [open, emailId]);

  // Close on Esc for quick keyboard dismiss.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const failed = detail?.status === "failed";
  const catLabel = CATEGORY_LABEL[detail?.category] || detail?.category || "Other";

  const copyResendId = () => {
    if (!detail?.resend_id) return;
    navigator.clipboard?.writeText(detail.resend_id);
    toast.success("Resend ID copied");
  };

  const doResend = async () => {
    const target = (resendTo || detail?.to || "").trim();
    if (!target) {
      toast.error("Enter a recipient email");
      return;
    }
    setResending(true);
    try {
      const { data } = await api.post(`/admin/email-log/${emailId}/resend`, {
        to: resendTo.trim() || null,
      });
      if (data.ok) {
        toast.success(`Resent to ${data.to}`);
        setShowResendForm(false);
        setResendTo("");
      } else {
        toast.error("Resend attempted but Resend rejected the send — check the log for details.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="email-preview-modal"
    >
      <div className="absolute inset-0 bg-charcoal-900/40 backdrop-blur-sm" />
      <div
        className="relative bg-charcoal-50 w-full max-w-3xl h-full shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="bg-white border-b border-black/5 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-charcoal-900 grid place-items-center shrink-0">
                <Mail className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <span className="text-[10.5px] font-semibold tracking-wide uppercase text-charcoal-500">
                {catLabel}
              </span>
              {detail && (
                failed ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                    <AlertTriangle className="w-3 h-3" /> Failed
                  </span>
                ) : detail.status === "sent" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Sent
                  </span>
                ) : null
              )}
            </div>
            <h2
              className="text-lg font-semibold text-charcoal-900 tracking-tight truncate"
              data-testid="email-preview-subject"
            >
              {detail?.subject || (loading ? "Loading…" : "—")}
            </h2>
            {detail && (
              <p className="text-xs text-charcoal-500 mt-1">
                To <span className="font-medium text-charcoal-700" data-testid="email-preview-to">{detail.to}</span>
                {" · "}
                <span className="font-mono">{fmtWhen(detail.sent_at)}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-full hover:bg-charcoal-100 text-charcoal-500 hover:text-charcoal-900 transition-colors shrink-0"
            data-testid="email-preview-close"
            aria-label="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Metadata strip */}
        {detail && (
          <div className="bg-white border-b border-black/5 px-6 py-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs">
            <MetaItem label="Category" value={catLabel} />
            <MetaItem label="Status" value={detail.status || "—"} valueClass={failed ? "text-red-600" : ""} />
            <MetaItem
              label="Resend ID"
              value={detail.resend_id ? `${detail.resend_id.slice(0, 8)}…` : "—"}
              mono
              trailing={detail.resend_id && (
                <button
                  onClick={copyResendId}
                  className="text-charcoal-400 hover:text-charcoal-700"
                  aria-label="Copy Resend ID"
                  data-testid="email-preview-copy-resend"
                >
                  <Copy className="w-3 h-3" />
                </button>
              )}
            />
            <MetaItem label="Reply-to" value={detail.reply_to || "—"} mono />
            {detail.reason && (
              <div className="col-span-2 md:col-span-4">
                <div className="text-charcoal-500 uppercase tracking-wide text-[10px] font-semibold mb-0.5">Reason</div>
                <div className="text-charcoal-800 text-xs" data-testid="email-preview-reason">{detail.reason}</div>
              </div>
            )}
            {failed && detail.error && (
              <div className="col-span-2 md:col-span-4">
                <div className="text-red-600 uppercase tracking-wide text-[10px] font-semibold mb-0.5">Error</div>
                <div className="text-red-700 text-xs font-mono">{detail.error}</div>
              </div>
            )}
          </div>
        )}

        {/* Rendered email body */}
        <div className="flex-1 overflow-hidden bg-charcoal-100">
          {loading && (
            <div className="h-full grid place-items-center text-charcoal-500 text-sm">Loading email…</div>
          )}
          {error && (
            <div className="h-full grid place-items-center text-red-600 text-sm px-6 text-center">{error}</div>
          )}
          {detail && !loading && !error && (
            detail.html_content ? (
              <iframe
                title="email-preview"
                sandbox=""
                srcDoc={detail.html_content}
                className="w-full h-full bg-white"
                data-testid="email-preview-iframe"
              />
            ) : (
              <div className="h-full grid place-items-center text-charcoal-500 text-sm px-6 text-center">
                No HTML body was captured for this send (older log entry).
              </div>
            )
          )}
        </div>

        {/* Footer — resend controls (only when we have an HTML body to replay) */}
        {detail && detail.html_content && (
          <footer className="bg-white border-t border-black/5 px-6 py-3">
            {showResendForm ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="email"
                  value={resendTo}
                  onChange={(e) => setResendTo(e.target.value)}
                  placeholder={detail.to || "recipient@example.com"}
                  className="flex-1 min-w-[220px] px-3 py-2 text-sm rounded-full border border-black/10 focus:outline-none focus:ring-1 focus:ring-charcoal-300"
                  data-testid="email-preview-resend-to"
                  autoFocus
                />
                <button
                  onClick={doResend}
                  disabled={resending}
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-charcoal-900 text-white hover:bg-charcoal-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="email-preview-resend-submit"
                >
                  <Send className="w-3.5 h-3.5" />
                  {resending ? "Sending…" : "Send"}
                </button>
                <button
                  onClick={() => { setShowResendForm(false); setResendTo(""); }}
                  disabled={resending}
                  className="text-sm text-charcoal-500 hover:text-charcoal-900 px-3 py-2 rounded-full hover:bg-charcoal-50 transition-colors"
                  data-testid="email-preview-resend-cancel"
                >
                  Cancel
                </button>
                <p className="basis-full text-[11px] text-charcoal-500 mt-0.5">
                  Leave blank to resend to the original recipient ({detail.to}). Uses the exact HTML captured above.
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-charcoal-500">
                  Need to re-fire this exact email? Resend uses the captured HTML — no re-run of the pipeline.
                </p>
                <button
                  onClick={() => setShowResendForm(true)}
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-charcoal-900 text-white hover:bg-charcoal-800 transition-colors"
                  data-testid="email-preview-resend-open"
                >
                  <Send className="w-3.5 h-3.5" />
                  Resend
                </button>
              </div>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function MetaItem({ label, value, mono = false, valueClass = "", trailing = null }) {
  return (
    <div>
      <div className="text-charcoal-500 uppercase tracking-wide text-[10px] font-semibold mb-0.5">{label}</div>
      <div className={`flex items-center gap-1.5 text-charcoal-800 ${mono ? "font-mono" : ""} ${valueClass}`}>
        <span className="truncate">{value}</span>
        {trailing}
      </div>
    </div>
  );
}
